import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Eye, Database, Share2, Lock, UserCheck, Bell, Clock, Mail, Globe } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA = { fontFamily: "'Lora', Georgia, serif" };

const Section = ({ icon: Icon, title, children }) => (
  <section className="border-b border-[#DCD4C6] pb-8 mb-8 last:border-0 last:mb-0">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 border border-[#DCD4C6] flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: "#F1EBE1", borderRadius: "2px" }}>
        <Icon size={14} strokeWidth={1.5} className="text-[#C17F59]" />
      </div>
      <h2 className="text-xl pt-1" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
        {title}
      </h2>
    </div>
    <div className="ml-11 space-y-3 text-sm text-[#5C554E] leading-relaxed" style={LORA}>
      {children}
    </div>
  </section>
);

const Tag = ({ children }) => (
  <span className="inline-flex items-center px-2 py-0.5 text-xs border border-[#DCD4C6] text-[#5C554E] mr-1.5 mb-1.5" style={{ ...LORA, borderRadius: "2px" }}>
    {children}
  </span>
);

export default function PrivacyPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16">

        {/* Header */}
        <div className="mb-12 animate-fade-in-up">
          <p className="section-label mb-3">Documents légaux</p>
          <h1 className="text-4xl sm:text-5xl mb-4" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
            Politique de confidentialité
          </h1>
          <div className="flex flex-wrap gap-4 text-xs text-[#9A8F86] mt-2" style={LORA}>
            <span>Voyage-r · Diego Lastra</span>
            <span>·</span>
            <span>Mise à jour : mai 2026</span>
            <span>·</span>
            <span>Conforme RGPD (Règlement UE 2016/679)</span>
          </div>
        </div>

        <div className="card-surface animate-fade-in-up" style={{ animationDelay: "0.1s" }}>

          {/* Art. 1 — Responsable */}
          <Section icon={UserCheck} title="Article 1 — Responsable du traitement">
            <p>
              Le responsable du traitement des données personnelles collectées via le site Voyage-r est :
            </p>
            <div className="bg-[#FDF8F3] border border-[#DCD4C6] p-4 rounded-sm mt-2">
              <p className="font-semibold text-[#2B2623]" style={SERIF}>Diego Lastra</p>
              <p>Conseiller voyage indépendant — Voyage-r</p>
              <p>Contact : <a href="mailto:diego@voyage-r.fr" className="text-[#C17F59] hover:underline">diego@voyage-r.fr</a></p>
            </div>
          </Section>

          {/* Art. 2 — Données collectées */}
          <Section icon={Database} title="Article 2 — Données personnelles collectées">
            <p>Dans le cadre de la fourniture du service Voyage-r, les données suivantes sont collectées :</p>

            <div className="space-y-4 mt-2">
              <div>
                <p className="font-medium text-[#2B2623] mb-1.5">Données d'identification et de contact</p>
                <div className="flex flex-wrap">
                  <Tag>Prénom et nom</Tag><Tag>Adresse email</Tag>
                </div>
              </div>
              <div>
                <p className="font-medium text-[#2B2623] mb-1.5">Données relatives au voyage</p>
                <div className="flex flex-wrap">
                  <Tag>Ville de départ</Tag><Tag>Destination</Tag><Tag>Dates de voyage</Tag>
                  <Tag>Nombre de voyageurs</Tag><Tag>Budget</Tag><Tag>Préférences (ambiances, hébergement, rythme…)</Tag>
                  <Tag>Contraintes médicales</Tag><Tag>Texte libre</Tag>
                </div>
              </div>
              <div>
                <p className="font-medium text-[#2B2623] mb-1.5">Données techniques</p>
                <div className="flex flex-wrap">
                  <Tag>Identifiant de session (cookie HTTP-only)</Tag>
                  <Tag>Abonnement push (endpoint + clés VAPID)</Tag>
                  <Tag>Préférence de langue</Tag>
                </div>
              </div>
              <div>
                <p className="font-medium text-[#2B2623] mb-1.5">Données de paiement</p>
                <p className="text-sm text-[#5C554E]" style={LORA}>
                  Les données bancaires sont traitées directement par <strong className="text-[#2B2623]">Stripe, Inc.</strong> (opérateur de paiement
                  certifié PCI-DSS). Voyage-r n'a jamais accès à vos coordonnées bancaires.
                </p>
              </div>
            </div>
          </Section>

          {/* Art. 3 — Finalités */}
          <Section icon={Eye} title="Article 3 — Finalités et bases légales">
            <table className="w-full text-xs border-collapse mt-1">
              <thead>
                <tr className="border-b-2 border-[#DCD4C6]">
                  <th className="text-left py-2 pr-4 text-[#8B5E34] uppercase tracking-wider" style={LORA}>Finalité</th>
                  <th className="text-left py-2 text-[#8B5E34] uppercase tracking-wider" style={LORA}>Base légale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DCD4C6]">
                {[
                  ["Création et gestion du compte", "Exécution du contrat (Art. 6.1.b RGPD)"],
                  ["Génération de l'itinéraire sur mesure", "Exécution du contrat"],
                  ["Envoi de l'itinéraire et notifications de statut", "Exécution du contrat"],
                  ["Traitement du paiement (via Stripe)", "Exécution du contrat"],
                  ["Email de relance 48h post-inscription", "Intérêt légitime (Art. 6.1.f RGPD)"],
                  ["Notifications push (validation du voyage)", "Consentement (Art. 6.1.a RGPD)"],
                  ["Prévention des fraudes et sécurité", "Obligation légale (Art. 6.1.c RGPD)"],
                ].map(([fin, base], i) => (
                  <tr key={i}>
                    <td className="py-2.5 pr-4 text-[#2B2623]" style={LORA}>{fin}</td>
                    <td className="py-2.5 text-[#5C554E] italic" style={LORA}>{base}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* Art. 4 — Durée de conservation */}
          <Section icon={Clock} title="Article 4 — Durée de conservation">
            <ul className="space-y-2">
              {[
                ["Données de compte (email, nom, mot de passe hashé)", "Jusqu'à suppression du compte + 30 jours de purge"],
                ["Données du voyage (itinéraire, préférences)", "Durée du voyage + 15 jours, puis archivage 1 an"],
                ["Logs de connexion et tokens de session", "30 jours glissants"],
                ["Abonnements push VAPID", "Jusqu'à désinscription ou expiration du token"],
                ["Données de facturation (référence Stripe)", "10 ans (obligation comptable légale)"],
              ].map(([item, duration], i) => (
                <li key={i} className="flex items-start gap-3 py-2 border-b border-[#DCD4C6] last:border-0">
                  <span className="text-[#C17F59] font-bold mt-0.5 flex-shrink-0">—</span>
                  <div>
                    <span className="font-medium text-[#2B2623]">{item}</span>
                    <span className="text-[#9A8F86] ml-1">→ {duration}</span>
                  </div>
                </li>
              ))}
            </ul>
          </Section>

          {/* Art. 5 — Sous-traitants */}
          <Section icon={Share2} title="Article 5 — Sous-traitants et transferts">
            <p>Voyage-r fait appel aux sous-traitants suivants, soumis à des engagements contractuels de confidentialité :</p>
            <div className="space-y-3 mt-3">
              {[
                {
                  name: "Stripe, Inc.",
                  role: "Traitement des paiements",
                  location: "États-Unis (clauses contractuelles types UE)",
                  link: "https://stripe.com/privacy",
                },
                {
                  name: "Resend, Inc.",
                  role: "Envoi d'emails transactionnels",
                  location: "États-Unis (clauses contractuelles types UE)",
                  link: "https://resend.com/privacy",
                },
                {
                  name: "Anthropic, PBC",
                  role: "Génération IA de l'itinéraire (Claude)",
                  location: "États-Unis — données traitées de manière confidentielle",
                  link: "https://www.anthropic.com/privacy",
                },
                {
                  name: "MongoDB Atlas",
                  role: "Hébergement de la base de données",
                  location: "UE (ou hébergement local selon configuration)",
                  link: "https://www.mongodb.com/legal/privacy-policy",
                },
              ].map((p, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#F9F6F0] border border-[#DCD4C6] p-3 rounded-sm">
                  <Globe size={14} strokeWidth={1.5} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#2B2623] text-sm" style={SERIF}>{p.name}</p>
                    <p className="text-xs text-[#5C554E]">{p.role} · {p.location}</p>
                    <a href={p.link} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-[#C17F59] hover:underline">{p.link}</a>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-[#9A8F86] italic">
              Voyage-r ne vend, ne loue et ne cède jamais vos données à des tiers à des fins commerciales.
            </p>
          </Section>

          {/* Art. 6 — Vos droits */}
          <Section icon={Lock} title="Article 6 — Vos droits (RGPD)">
            <p>Conformément au RGPD, vous disposez des droits suivants sur vos données :</p>
            <ul className="space-y-2 mt-3">
              {[
                ["Droit d'accès", "Obtenir une copie de vos données personnelles détenues par Voyage-r."],
                ["Droit de rectification", "Corriger des données inexactes ou incomplètes."],
                ["Droit à l'effacement", "Supprimer votre compte et l'ensemble de vos données (« droit à l'oubli »)."],
                ["Droit à la portabilité", "Recevoir vos données dans un format structuré et lisible par machine."],
                ["Droit d'opposition", "S'opposer au traitement fondé sur l'intérêt légitime (ex: email de relance)."],
                ["Droit de retrait du consentement", "Désactiver les notifications push à tout moment depuis la cloche dans la barre de navigation."],
              ].map(([right, desc], i) => (
                <li key={i} className="flex items-start gap-3 py-2.5 border-b border-[#DCD4C6] last:border-0">
                  <span className="inline-block mt-0.5 px-2 py-0.5 bg-[#C17F59] text-[#F9F6F0] text-xs whitespace-nowrap" style={{ ...LORA, borderRadius: "2px" }}>
                    {right}
                  </span>
                  <span className="text-sm text-[#5C554E]">{desc}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 p-4 bg-[#FDF8F3] border border-[#DCD4C6] rounded-sm">
              <p>
                Pour exercer ces droits, contactez :{" "}
                <a href="mailto:diego@voyage-r.fr" className="text-[#C17F59] hover:underline font-medium">
                  diego@voyage-r.fr
                </a>
              </p>
              <p className="mt-1.5 text-xs text-[#9A8F86] italic">
                Délai de réponse : 30 jours maximum. En cas de litige non résolu, vous pouvez saisir la{" "}
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#C17F59] hover:underline">
                  CNIL (cnil.fr)
                </a>.
              </p>
            </div>
          </Section>

          {/* Art. 7 — Cookies */}
          <Section icon={Bell} title="Article 7 — Cookies et stockage local">
            <p>Voyage-r utilise un nombre minimal de mécanismes de stockage, exclusivement nécessaires au fonctionnement du service :</p>
            <div className="space-y-2 mt-3">
              {[
                { name: "access_token (cookie HTTP-only)", purpose: "Maintien de la session de connexion", duration: "Durée de session" },
                { name: "voyage-r-lang (localStorage)", purpose: "Mémorisation de la langue choisie (FR/EN)", duration: "Permanent (jusqu'à effacement)" },
                { name: "pwa-install-dismissed (localStorage)", purpose: "Mémorisation du choix d'installation PWA", duration: "Permanent" },
              ].map((c, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 text-xs py-2 border-b border-[#DCD4C6] last:border-0" style={LORA}>
                  <code className="text-[#C17F59] font-mono bg-[#F1EBE1] px-1.5 py-0.5 rounded-sm text-xs break-all">{c.name}</code>
                  <span className="text-[#5C554E]">{c.purpose}</span>
                  <span className="text-[#9A8F86] italic">{c.duration}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-[#9A8F86] italic">
              Aucun cookie publicitaire, de tracking ou analytique tiers n'est utilisé sur voyage-r.fr.
            </p>
          </Section>

          {/* Art. 8 — Sécurité */}
          <Section icon={Lock} title="Article 8 — Sécurité">
            <p>
              Voyage-r met en œuvre les mesures de sécurité suivantes : chiffrement TLS/HTTPS, mots de passe
              hashés (bcrypt), jetons JWT à durée limitée, accès base de données restreint par IP. En cas de
              violation de données, les personnes concernées seront notifiées dans les 72 heures conformément
              à l'article 33 du RGPD.
            </p>
          </Section>

          {/* Art. 9 — Contact DPO */}
          <section className="pt-2">
            <p className="text-xs text-[#9A8F86] italic leading-relaxed" style={LORA}>
              <strong style={{ color: "#5C554E" }}>Contact & réclamations.</strong>{" "}
              Pour toute question relative à la protection de vos données :{" "}
              <a href="mailto:diego@voyage-r.fr" className="text-[#C17F59] hover:underline">diego@voyage-r.fr</a>.
              Autorité de contrôle compétente : Commission Nationale de l'Informatique et des Libertés (CNIL) —{" "}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#C17F59] hover:underline">cnil.fr</a>.
            </p>
          </section>

        </div>

        {/* Footer nav */}
        <div className="flex flex-wrap items-center gap-4 mt-10 text-xs text-[#9A8F86]" style={LORA}>
          <Link to="/" className="hover:text-[#C17F59] transition-colors">← Retour à l'accueil</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-[#C17F59] transition-colors">Conditions Générales de Vente</Link>
          <span>·</span>
          <a href="mailto:diego@voyage-r.fr" className="hover:text-[#C17F59] transition-colors">Contact</a>
        </div>

      </main>
    </div>
  );
}
